<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div class="config-item-editor">
    <!-- Header with key name and type badge -->
    <div class="editor-header">
      <h3 class="config-key">{{ item.key }}</h3>
      <span class="type-badge">{{ $t(`config.types.${item.type}`) }}</span>
    </div>

    <!-- Description -->
    <p v-if="description?.descriptionKey" class="config-description">
      {{ $t(description.descriptionKey) }}
    </p>

    <!-- Define/Undefine buttons -->
    <div class="definition-actions">
      <div v-if="!item.isDefined" class="undefined-container">
        <PrimeButton
          :label="$t('config.buttons.define')"
          icon="ri-add-line"
          class="p-button-outlined"
          @click="defineValue"
        />
        <small class="help-text">
          {{ $t("config.notDefined") }}
        </small>
      </div>
      <div v-else class="defined-actions">
        <PrimeButton
          :label="$t('config.buttons.undefine')"
          icon="ri-delete-bin-line"
          severity="danger"
          class="p-button-outlined p-button-sm"
          @click="undefineValue"
        />
      </div>
    </div>

    <!-- Editor based on type -->
    <div v-if="item.isDefined" class="editor-content">
      <!-- Boolean editor -->
      <div v-if="item.type === 'boolean'" class="editor-field">
        <label>{{ $t("config.table.valueHeader") }}</label>
        <PrimeToggleSwitch
          :model-value="item.value"
          @update:model-value="updateValue"
        />
      </div>

      <!-- String editor -->
      <div v-else-if="item.type === 'string'" class="editor-field">
        <label>{{ $t("config.table.valueHeader") }}</label>
        <PrimeInputText
          :model-value="item.value"
          class="w-full"
          @update:model-value="updateValue"
        />
      </div>

      <!-- Number editor -->
      <div v-else-if="item.type === 'number'" class="editor-field">
        <label>{{ $t("config.table.valueHeader") }}</label>
        <PrimeInputNumber
          :model-value="item.value"
          class="w-full"
          @update:model-value="updateValue"
        />
      </div>

      <!-- Array editor -->
      <div
        v-else-if="item.type === 'array'"
        class="editor-field editor-field-array"
      >
        <label>{{ $t("config.table.valueHeader") }}</label>
        <ArrayEditor
          :is-metadata="item.key === 'METADATA'"
          :model-value="item.value"
          @update:model-value="updateValue"
        />
      </div>

      <!-- Object editor (read-only JSON display) -->
      <div v-else-if="item.type === 'object'" class="editor-field">
        <label>{{ $t("config.table.valueHeader") }}</label>
        <pre class="object-display">{{
          JSON.stringify(item.value, null, 2)
        }}</pre>
      </div>
    </div>

    <!-- Default value hint -->
    <div v-if="description?.default !== undefined" class="default-hint">
      <i class="ri-information-line" aria-hidden="true"></i>
      <span>{{ $t("config.splitView.defaultValue") }}: </span>
      <code>{{ formatDefaultValue(description.default) }}</code>
    </div>
  </div>
</template>

<script setup>
import PrimeToggleSwitch from "primevue/toggleswitch";
import PrimeInputText from "primevue/inputtext";
import PrimeInputNumber from "primevue/inputnumber";
import PrimeButton from "primevue/button";
import ArrayEditor from "@/components/config/ArrayEditor.vue";

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  description: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["update:value", "define", "undefine"]);

const updateValue = (newValue) => {
  emit("update:value", newValue);
};

const defineValue = () => {
  emit("define");
};

const undefineValue = () => {
  emit("undefine");
};

const formatDefaultValue = (value) => {
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};
</script>

<style scoped>
.config-item-editor {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  height: 100%;
  overflow-y: auto;
}

.editor-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.config-key {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--p-text-color);
  word-break: break-all;
}

.type-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  background-color: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
  border-radius: 4px;
  white-space: nowrap;
}

.config-description {
  font-size: 0.9rem;
  color: var(--p-text-muted-color);
  margin: 0;
  line-height: 1.5;
}

.definition-actions {
  margin-top: 0.5rem;
}

.undefined-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  padding: 1rem;
  background-color: var(--p-content-background);
  border: 1px dashed var(--p-content-border-color);
  border-radius: 6px;
}

.undefined-container .help-text {
  color: var(--p-text-muted-color);
  font-style: italic;
  font-size: 0.85rem;
}

.defined-actions {
  display: flex;
  gap: 0.5rem;
}

.editor-content {
  margin-top: 0.5rem;
}

.editor-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.editor-field label {
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.editor-field-array {
  flex: 1;
  min-height: 200px;
}

.object-display {
  background-color: var(--p-surface-hover);
  border: 1px solid var(--p-surface-border);
  border-radius: 4px;
  padding: 1rem;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 0.8rem;
  max-height: 400px;
  overflow-y: auto;
  margin: 0;
}

.default-hint {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  background-color: var(--p-content-background);
  border-radius: 4px;
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
}

.default-hint i {
  color: var(--p-primary-color);
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.default-hint code {
  background-color: var(--p-surface-hover);
  padding: 0.1rem 0.3rem;
  border-radius: 3px;
  font-size: 0.8rem;
  word-break: break-all;
  max-width: 100%;
  overflow-wrap: break-word;
}
</style>
